import { Button } from 'components/atoms/Button';
import { useAiMetadata } from 'contexts/AIMetadataProvider';
import { useRevenueAiMetadata } from 'contexts/AIMetadataProvider/useRevenueAiMetadata';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Invoice } from 'types/invoice.types';
import { ArrowRightIcon } from 'utils/icons';
import showToast from 'utils/toast';
import { useIntercom } from './intercom.hook';
import useWorldTheme, { worldThemeContext } from './shared/useWorldTheme';
import useInvalidateResources from './useInvalidateResources';
import useModal from './useModal';
import usePusher from './usePusher';
import { useShouldShowTaxAdvisor } from './TaxAdvisor/useShouldShowTaxAdvisor';

type Reason =
  | 'NOT_INVOICE'
  | 'NOT_SENT'
  | 'NOT_LOCAL_CURRENCY'
  | 'NOT_EQUAL_TRANSACTION_AMOUNT'
  | 'TAX_LOCKED'
  | 'UNKNOWN';

type EventObject = {
  markedAsPaid: false;
  reason?: Reason;
  alreadyPaid?: boolean;
  revenue: Invoice;
};

type EventData = null | EventObject[];

const useRevenueStatusEvent = () => {
  const { t } = useTranslation();
  const channel = usePusher();
  const invalidateResources = useInvalidateResources();
  const [content, api] = useModal({
    name: 'revenues status update event modal',
  });

  const triggerAlert: typeof showToast = (props, config) => {
    return showToast(props, {
      ...config,
      position: 'bottom-left',
    });
  };

  useEffect(() => {
    if (!channel) return;

    const handler = (maybeUnfilteredData: EventData) => {
      if (!Array.isArray(maybeUnfilteredData)) return;

      const data = maybeUnfilteredData.filter((item) => !item.alreadyPaid);
      if (!data.length) return;

      const succeded = data.filter((item) => item.markedAsPaid);
      if (succeded.length) {
        invalidateResources('revenueUpdateStatusEvent');
      }
      if (data.length === succeded.length) {
        if (succeded.length === 1) {
          return triggerAlert(
            {
              title: t('revenue_status_update.marked_as_paid.success.single', {
                revenueNumber: succeded[0].revenue.revenueNumber,
              }),
            },
            {
              type: 'success',
            },
          );
        }
        return triggerAlert(
          {
            title: t('revenue_status_update.marked_as_paid.success.multi'),
          },
          {
            type: 'success',
            onClick: () =>
              api.open(() => (
                <div className="p-6">
                  <ul className="list-disc">
                    {succeded.map((item, i) => (
                      <li
                        key={item.revenue.revenueNumber}
                        className={`text-md text-primary ${
                          i === 0 ? 'pb-2' : 'py-2'
                        }`}
                      >
                        <span className="font-semibold">
                          {t('revenue_status_update.revenue_title', {
                            revenueNumber: item.revenue.revenueNumber,
                          })}
                        </span>
                        <span className="block">
                          {t(
                            'revenue_status_update.marked_as_paid.success.single',
                            {
                              revenueNumber: item.revenue.revenueNumber,
                            },
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3">
                    <SupportButton revenue={succeded[0].revenue} />
                  </div>
                </div>
              )),
          },
        );
      }

      const failed = data.filter((item) => !item.markedAsPaid);

      return triggerAlert(
        {
          title:
            failed.length > 1
              ? t('revenue_status_update.marked_as_paid.fail.multi')
              : t('revenue_status_update.marked_as_paid.fail.single', {
                  revenueNumber: failed[0].revenue.revenueNumber,
                }),
        },
        {
          type: 'error',
          autoClose: false,
          onClick: () =>
            api.open(() => (
              <div className="p-6">
                <ul className="list-disc">
                  {failed.map((item, i) => (
                    <li
                      key={item.revenue.revenueNumber}
                      className={`text-md text-primary ${
                        i === 0 ? 'pb-2' : 'py-2'
                      }`}
                    >
                      <span className="font-semibold">
                        {t('revenue_status_update.revenue_title', {
                          revenueNumber: item.revenue.revenueNumber,
                        })}
                      </span>
                      <span className="block">
                        {t(
                          `revenue_status_update.marked_as_paid.fail.reason.${item.reason!.toLowerCase()}`,
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3">
                  <SupportButton revenue={failed[0].revenue} />
                </div>
              </div>
            )),
        },
      );
    };

    channel.bind('RevenuePaymentReconciliation.processed', handler);
    return () => {
      channel.unbind('RevenuePaymentReconciliation.processed', handler);
    };
  }, [channel, invalidateResources]);

  return content;
};

const UseRevenueStatusEvent = () => {
  return (
    <worldThemeContext.Provider value={'bank'}>
      {useRevenueStatusEvent()}
    </worldThemeContext.Provider>
  );
};

const SupportButton: FC<{ revenue: Invoice }> = (props) => {
  const { t } = useTranslation();
  const shouldShowTaxAdvisor = useShouldShowTaxAdvisor();

  const { showIntercom } = useIntercom();
  const { setIsHelperOpen } = useAiMetadata();

  useRevenueAiMetadata(props.revenue);

  const worldColor = useWorldTheme();

  const openChat = useCallback(() => {
    if (shouldShowTaxAdvisor) {
      setIsHelperOpen(true);
    } else {
      showIntercom();
    }
  }, [setIsHelperOpen, shouldShowTaxAdvisor, showIntercom]);

  return (
    <Button
      onClick={openChat}
      structure="text"
      color={worldColor}
      icon={ArrowRightIcon}
      iconPlacement="right"
    >
      {shouldShowTaxAdvisor
        ? t('repository.document_form.ask_ai_cta')
        : t('talk_to_an_expert')}
    </Button>
  );
};

export default UseRevenueStatusEvent;
